
/**
 * Any dashboard API calls need to be on this page.
 * Use CacheKeys QUERY_KEYS.dashboard.root to invalidate.
 */
import { useMemo } from 'react';
import { GetDashboardAllCounts, GetMentoredStudentsCount, GetUnreadCouresNotificationCount } from '../../services/DashBoardServices';
import { GetAllLecturers, GetCommonContentReReg } from '../../services/ProgrammeServices';
import { appQueryClient } from '../appQueryClient';
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from '../queryConsts';
import { useBaseReactQuery } from './useBaseReactQuery';

/**
 * Invalidates all dashboard-related queries.
 * All active queries in dashboard will automatically refetch eventually.
 */
export function invalidateDashboardQueries() {
    return appQueryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.dashboard.root]
    });
}

/**
 * Dashboard - Get counts to display on the dashboard - PERSISTED
 * @param {object} filters Parameters to fetch.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useDashboardAllCounts(filterIntake, filterSchool, filterLearningMethod, filterLecturer) {
    const filters = useMemo(() => ({
        globalIntake: filterIntake?.newValue ?? [],
        globalSchool: filterSchool?.newValue ?? [],
        globalLearningMethod: filterLearningMethod?.newValue ?? [],
        globalLecturer: filterLecturer?.newValue ?? []
    }), [filterIntake, filterSchool, filterLearningMethod, filterLecturer]);

    return useBaseReactQuery(
        [QUERY_KEYS.dashboard.root, QUERY_KEYS.dashboard.allCounts, filters],
        async () => {
            const stringifiedValues = {
                globalIntake: JSON.stringify(filters.globalIntake),
                globalSchool: JSON.stringify(filters.globalSchool),
                globalLearningMethod: JSON.stringify(filters.globalLearningMethod),
                globalLecturer: JSON.stringify(filters.globalLecturer)
            }
            const { data } = await GetDashboardAllCounts(stringifiedValues);
            return data;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LIVE
        }
    );
}

/**
 * Dashboard - For getting all the lecturers - PERSISTED
 * Custom for drop downs - More time than usual for caching.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useAllLecturers() {
    return useBaseReactQuery(
        [QUERY_KEYS.dashboard.root, QUERY_KEYS.dashboard.allLecturers],
        async () => {
            const { data } = await GetAllLecturers();
            return data;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP
        }
    );
}

/**
 * Dashboard - Get common content for Rereg - PERSISTED
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useCommonContentReReg() {
    return useBaseReactQuery(
        [QUERY_KEYS.dashboard.root, QUERY_KEYS.dashboard.commonContentReReg],
        async () => {
            const { data } = await GetCommonContentReReg();
            return data;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP
        }
    );
}

/**
 * Dashboard - For Mentored Student counts - PERSISTED
 * @param {object} filters Parameters to fetch.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useMentoredStudentsCount(filters) {
    return useBaseReactQuery(
        [QUERY_KEYS.dashboard.root, QUERY_KEYS.dashboard.mentoredStudents, filters],
        async () => {
            const { data } = await GetMentoredStudentsCount(filters);
            return data;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
            retry: 5
        }
    );
}

/**
 * Dashboard - Get counts to display for unread course notifications - PERSISTED
 * @param {object} filters Parameters to fetch.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useUnreadCourseNotificationCount(filters) {
    return useBaseReactQuery(
        [QUERY_KEYS.dashboard.root, QUERY_KEYS.dashboard.unreadCourseNotifications, filters],
        async () => {
            const { data } = await GetUnreadCouresNotificationCount(filters);
            return data.allUnreadMessage;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
            retry: 5
        }
    );
}
